// Mirror of hawk/core/types/monitoring.py. Update together.

// 'deleted' specifically means torn down via delete *and* no output: a deleted
// job that did produce output keeps its warehouse-derived outcome
// ('complete'/'failed') — the results still exist.
export type JobStatus =
  | 'pending'
  | 'running'
  | 'waiting_for_capacity'
  | 'importing'
  | 'stuck'
  | 'complete'
  | 'failed'
  | 'deleted';

export type ImportStatus = 'pending' | 'importing' | 'success' | 'failed';

export interface ImportStatusDetail {
  location: string;
  status: ImportStatus;
  error_message: string | null;
}

export interface ImportSummary {
  total: number;
  pending: number;
  importing: number;
  success: number;
  failed: number;
  details: ImportStatusDetail[];
}

export interface EvalSetDetails {
  import_summary: ImportSummary | null;
  warehouse_sample_count: number;
  trouble_reason: string | null;
}

export interface ScanDetails {
  scan_id: string;
  scan_name: string | null;
  errors: string[] | null;
  scanner_result_count: number;
}

export interface PodCondition {
  type: string;
  status: string;
  reason: string | null;
  message: string | null;
}

export interface ContainerStatus {
  name: string;
  ready: boolean;
  state: string;
  reason: string | null;
  message: string | null;
  restart_count: number;
}

export interface PodEvent {
  type: string;
  reason: string;
  message: string;
  count: number;
  timestamp: string | null;
  field_path: string | null;
}

export interface PodStatusInfo {
  name: string;
  namespace: string;
  phase: string;
  component: string | null;
  job_type: string | null;
  sample_id: string | null;
  conditions: PodCondition[];
  container_statuses: ContainerStatus[];
  events: PodEvent[];
  creation_timestamp: string | null;
  pod_ip: string | null;
}

export interface PodStatusData {
  pods: PodStatusInfo[];
}

export interface JobStatusResponse {
  job_type: 'eval_set' | 'scan' | 'unknown';
  status: JobStatus;
  pod_status: PodStatusData | null;
  /**
   * Set when the K8s pod-status fetch itself failed, as opposed to
   * succeeding and finding no pods. `pod_status` is null in both cases,
   * which is ambiguous on its own -- null here means the fetch either
   * succeeded or was never attempted.
   */
  pod_status_error: string | null;
  /** Sandbox pods the scheduler currently reports Unschedulable. */
  unschedulable_sandboxes: number;
  details: EvalSetDetails | ScanDetails;
}

export function isEvalSetDetails(
  details: EvalSetDetails | ScanDetails
): details is EvalSetDetails {
  return !('scan_id' in details);
}

export function isTerminal(
  status: JobStatus
): status is 'complete' | 'failed' | 'deleted' {
  return status === 'complete' || status === 'failed' || status === 'deleted';
}

// --- Live eval-set status (per-task / per-sample drilldown) ---------------

export interface MetricsQueryResult {
  value: number | null;
  unit: string | null;
}

export type SamplePhase =
  | 'waiting'
  | 'init'
  | 'running'
  | 'scoring'
  | 'completed'
  | 'errored'
  | 'limit'
  | 'canceled';

export interface EventBrief {
  type: string;
  description: string | null;
  timestamp: string | null;
  duration_seconds: number | null;
  pending: boolean;
}

export interface SampleStatus {
  id: string;
  epoch: number;
  uuid: string | null;
  phase: SamplePhase;
  completed: boolean;
  limit: string | null;
  error: string | null;
  retries: number | null;
  started_at: string | null;
  completed_at: string | null;
  total_time: number | null;
  working_time: number | null;
  total_tokens: number | null;
  latest_score: string | null;
  last_events: EventBrief[];
}

export interface StatusProgress {
  total: number;
  completed: number;
  running: number;
  errored: number;
  canceled: number;
  limit: number;
  waiting: number;
}

export interface TaskStatus {
  task_name: string;
  task_id: string | null;
  log_file: string;
  eval_status: string | null;
  live: boolean;
  trouble_reason: string | null;
  progress: StatusProgress;
  samples: SampleStatus[];
}

export interface WaitingSample {
  sample_id: string | null;
  pod_name: string;
  phase: string;
  reason: string | null;
  detail: string | null;
}

export interface EvalSetStatus {
  job_id: string;
  status: JobStatus;
  created_by: string | null;
  is_owner_or_admin: boolean;
  trouble_reason: string | null;
  pods: PodStatusData | null;
  metrics: Record<string, MetricsQueryResult> | null;
  progress: StatusProgress;
  tasks: TaskStatus[];
  waiting_samples: WaitingSample[];
  errors: Record<string, string>;
}

export interface EvalSetStatusResponse {
  data: EvalSetStatus;
  etag: string;
}
